import { Check } from "lucide-react";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import {
  isCecPaidFeatureIncluded,
  type CecPaywallFeature,
  type CecPlanId,
} from "./cecPlanFeatures";

export type { CecPlanId } from "./cecPlanFeatures";

interface CecPaywallPlanCardProps {
  actions: ReactNode;
  annualBillingLabel: string;
  annualPlanLabel: string;
  annualPrice: string;
  apprenticeBillingLabel: string;
  apprenticeColumnLabel: string;
  apprenticePlanLabel: string;
  apprenticePrice: string;
  features: CecPaywallFeature[];
  freeLabel: string;
  onSelectPlan: (plan: CecPlanId) => void;
  savingsLabel: string;
  selectedPlan: CecPlanId;
}

export function CecPaywallPlanCard({
  actions,
  annualBillingLabel,
  annualPlanLabel,
  annualPrice,
  apprenticeBillingLabel,
  apprenticeColumnLabel,
  apprenticePlanLabel,
  apprenticePrice,
  features,
  freeLabel,
  onSelectPlan,
  savingsLabel,
  selectedPlan,
}: CecPaywallPlanCardProps) {
  const { t } = useTranslation();
  const paidPlanLabel =
    selectedPlan === "apprentice" ? apprenticeColumnLabel : annualPlanLabel;

  return (
    <div className="w-full">
      <section className="rounded-[28px] bg-white p-3 shadow-[0_10px_20px_rgba(10,13,18,0.08)]" aria-label={t("paywall.plans_aria", { plan: "CEC" })}>
        <div className="grid grid-cols-2 gap-8 px-2 pb-3 pt-2">
          <button
            aria-pressed={selectedPlan === "annual"}
            className={`border-b-2 pb-3 text-left outline-none transition-colors focus-visible:ring-2 focus-visible:ring-[var(--dp-brand)] ${selectedPlan === "annual" ? "border-[var(--dp-brand)]" : "border-[#dddfe4]"}`}
            onClick={() => onSelectPlan("annual")}
            type="button"
          >
            <span className={`block text-lg font-bold leading-7 ${selectedPlan === "annual" ? "text-[var(--dp-brand)]" : "text-[#8c929c]"}`}>{annualPlanLabel}</span>
            <span className={`mt-1 block text-[36px] font-bold leading-[43px] ${selectedPlan === "annual" ? "text-[#0a0c11]" : "text-[#8c929c]"}`}>{annualPrice}</span>
            <span className="mt-1 block text-base text-[#8c929c]">{annualBillingLabel}</span>
          </button>
          <button
            aria-pressed={selectedPlan === "apprentice"}
            className={`border-b-2 pb-3 text-left outline-none transition-colors focus-visible:ring-2 focus-visible:ring-[var(--dp-brand)] ${selectedPlan === "apprentice" ? "border-[var(--dp-brand)]" : "border-[#dddfe4]"}`}
            onClick={() => onSelectPlan("apprentice")}
            type="button"
          >
            <span className={`block text-lg font-bold leading-7 ${selectedPlan === "apprentice" ? "text-[var(--dp-brand)]" : "text-[#8c929c]"}`}>{apprenticePlanLabel}</span>
            <span className="mt-1 block text-[36px] font-bold leading-[43px] text-[#0a0c11]">{apprenticePrice}</span>
            <span className="mt-1 flex items-center gap-3 text-base text-[#5b616d]">
              {apprenticeBillingLabel}
              <span className="rounded-full bg-[var(--dp-brand-soft)] px-3 py-1 text-sm font-medium text-[var(--dp-brand)]">{savingsLabel}</span>
            </span>
          </button>
        </div>

        <div className="relative overflow-hidden rounded-2xl border border-[#dddfe4]">
          <div aria-hidden="true" className="absolute inset-y-0 right-0 w-[108px] bg-[var(--dp-brand-soft)]" />
          <div className="relative grid grid-cols-[minmax(0,1fr)_92px_108px] items-center">
            <span aria-hidden="true" />
            <span className="py-4 text-center text-base font-bold">{freeLabel}</span>
            <span className="py-4 text-center text-base font-bold text-[var(--dp-brand)]">{paidPlanLabel}</span>
          </div>
          {features.map((feature) => {
            const paidIncluded = isCecPaidFeatureIncluded(feature, selectedPlan);
            return (
              <div className="relative grid min-h-14 grid-cols-[minmax(0,1fr)_92px_108px] items-center border-t border-[#efefef]" key={feature.label}>
                <p className="px-3 text-base font-medium text-[#0a0c11]">{feature.label}</p>
                <span className="flex justify-center text-[#8c929c]">
                  {feature.freeIncluded ? <Check aria-label={t("paywall.feature_included_free", { feature: feature.label })} className="size-4" /> : "—"}
                </span>
                <span className="flex justify-center text-[var(--dp-brand)]">
                  {paidIncluded ? (
                    <Check aria-label={t("paywall.feature_included_plan", { feature: feature.label, plan: paidPlanLabel })} className="size-4" />
                  ) : (
                    <span className="text-[#5b616d]">—</span>
                  )}
                </span>
              </div>
            );
          })}
        </div>
      </section>
      <div className="mt-8">{actions}</div>
    </div>
  );
}
